import { useEffect, useState } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";

export const CAL_CLASSES = {
  caption_label: "text-sm font-bold text-white",
  nav_button: "h-7 w-7 inline-flex items-center justify-center rounded-full text-white/50 transition-colors hover:bg-white/10 hover:text-white",
  head_cell: "w-9 text-[10px] font-bold uppercase tracking-wider text-white/30",
  cell: "relative p-0 text-center",
  day: "h-9 w-9 rounded-full text-sm text-white/80 transition-colors hover:bg-white/10 hover:text-white",
  day_selected: "!bg-[#00FF66] !text-black font-black",
  day_today: "ring-1 ring-inset ring-[#00FF66]/50 text-[#00FF66]",
  day_outside: "text-white/20",
  day_disabled: "text-white/15 pointer-events-none",
};

const pad = (n) => String(n).padStart(2, "0");
const fmtTime = (h, m) => `${((h + 11) % 12) + 1}:${pad(m)} ${h < 12 ? "AM" : "PM"}`;

function Stepper({ value, min, max, onUp, onDown, onType, keyUp = onUp, keyDown = onDown, testid }) {
  const [draft, setDraft] = useState(value);
  const [focused, setFocused] = useState(false);
  useEffect(() => { if (!focused) setDraft(value); }, [value, focused]);
  const change = (e) => {
    const v = e.target.value.replace(/\D/g, "").slice(-2);
    setDraft(v);
    const n = parseInt(v, 10);
    if (!Number.isNaN(n) && n >= min && n <= max) onType(n);
  };
  const key = (e) => {
    if (e.key === "ArrowUp") { e.preventDefault(); keyUp(); }
    else if (e.key === "ArrowDown") { e.preventDefault(); keyDown(); }
    else if (e.key === "Enter") e.currentTarget.blur();
  };
  return (
    <div className="flex flex-col items-center">
      <button type="button" tabIndex={-1} onClick={onUp} className="rounded-md p-0.5 text-white/40 transition-colors hover:bg-white/5 hover:text-[#00FF66]" data-testid={`${testid}-up`}><ChevronUp className="h-4 w-4" /></button>
      <input
        type="text"
        inputMode="numeric"
        value={focused ? draft : value}
        onChange={change}
        onKeyDown={key}
        onFocus={(e) => { setFocused(true); setDraft(value); e.target.select(); }}
        onBlur={() => setFocused(false)}
        className="font-display w-11 rounded-md bg-transparent text-center text-2xl font-bold tabular-nums text-white caret-[#00FF66] outline-none transition-colors focus:bg-white/[0.06] focus:ring-1 focus:ring-[#00FF66]/60"
        aria-label={testid.endsWith("hour") ? "Hour" : "Minute"}
        data-testid={testid}
      />
      <button type="button" tabIndex={-1} onClick={onDown} className="rounded-md p-0.5 text-white/40 transition-colors hover:bg-white/5 hover:text-[#00FF66]" data-testid={`${testid}-down`}><ChevronDown className="h-4 w-4" /></button>
    </div>
  );
}

// On-brand date + time picker (replaces the browser's native datetime control).
export function DateTimePicker({ initial, onPick, testid = "dtp", children, cta = (d, t) => `Remind me ${d} · ${t}` }) {
  const [day, setDay] = useState(() => new Date(initial.getFullYear(), initial.getMonth(), initial.getDate()));
  const [hour, setHour] = useState(initial.getHours());
  const [minute, setMinute] = useState(Math.ceil(initial.getMinutes() / 5) * 5 % 60);
  const when = new Date(day.getFullYear(), day.getMonth(), day.getDate(), hour, minute);
  const past = when <= new Date();
  const today = new Date(); today.setHours(0, 0, 0, 0);

  return (
    <div data-testid={testid}>
      <Calendar
        mode="single"
        selected={day}
        onSelect={(d) => d && setDay(d)}
        disabled={{ before: today }}
        className="flex justify-center p-0"
        classNames={CAL_CLASSES}
      />

      <div className="mt-3 flex items-center justify-center gap-1 rounded-xl border border-white/[0.08] bg-white/[0.02] py-2">
        <Stepper testid={`${testid}-hour`} value={String(((hour + 11) % 12) + 1)} min={1} max={12} onType={(n) => setHour((h) => (n % 12) + (h >= 12 ? 12 : 0))} onUp={() => setHour((h) => (h + 1) % 24)} onDown={() => setHour((h) => (h + 23) % 24)} />
        <span className="font-display pb-0.5 text-2xl font-bold text-white/30">:</span>
        <Stepper testid={`${testid}-minute`} value={pad(minute)} min={0} max={59} onType={setMinute} keyUp={() => setMinute((m) => (m + 1) % 60)} keyDown={() => setMinute((m) => (m + 59) % 60)} onUp={() => setMinute((m) => (m + 5) % 60)} onDown={() => setMinute((m) => (m + 55) % 60)} />
        <div className="ml-2 flex flex-col gap-1">
          {["AM", "PM"].map((p) => {
            const active = (p === "AM") === (hour < 12);
            return (
              <button
                key={p}
                type="button"
                onClick={() => !active && setHour((h) => (h + 12) % 24)}
                className={`rounded-md px-2 py-0.5 text-[11px] font-black transition-colors ${active ? "bg-[#00FF66] text-black" : "text-white/40 hover:text-white"}`}
                data-testid={`${testid}-${p.toLowerCase()}`}
              >
                {p}
              </button>
            );
          })}
        </div>
      </div>

      {children}

      <button
        type="button"
        onClick={() => !past && onPick(when)}
        disabled={past}
        className="mt-3 w-full rounded-full bg-[#00FF66] py-2.5 text-xs font-black text-black transition-transform duration-200 hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:bg-white/10 disabled:text-white/30 disabled:hover:translate-y-0"
        data-testid={`${testid}-set`}
      >
        {past ? "Pick a time in the future" : cta(when.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" }), fmtTime(hour, minute))}
      </button>
    </div>
  );
}
